import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { useSearchParams } from 'react-router-dom';

const ACADEMY_URL = new URL(process.env.REACT_APP_ACADEMY_URL || '/academy', window.location.origin).href.replace(/\/$/, '');
const ACADEMY_ORIGIN = ACADEMY_URL ? new URL(ACADEMY_URL).origin : '';

export default function AcademyFrame({ actor = 'admin', onDirtyChange }) {
  const [searchParams] = useSearchParams();
  const requestedView = searchParams.get("mode");
  const frameRef = useRef(null);
  const auth = useRef({ token: '', until: 0 });
  const request = useRef(null);
  const refreshTimer = useRef(null);
  const active = useRef(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const [canEdit, setCanEdit] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [view, setView] = useState(actor === 'admin' && requestedView !== 'learn' ? 'manage' : 'learn');
  useEffect(() => {
    if (!dirty && canEdit && ['learn', 'manage'].includes(requestedView)) setView(requestedView);
  }, [requestedView, canEdit, dirty]);
  useEffect(() => {onDirtyChange?.(dirty); return () => onDirtyChange?.(false);}, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!dirty) return undefined;
    const beforeUnload = e => {e.preventDefault(); e.returnValue = '';};
    const beforeLink = e => {
      const link = e.target.closest?.('a[href]');
      if (link && !window.confirm('Есть несохранённые изменения в академии. Покинуть редактор?')) {e.preventDefault(); e.stopPropagation();}
    };
    window.addEventListener('beforeunload', beforeUnload);
    document.addEventListener('click', beforeLink, true);
    window.Telegram?.WebApp?.enableClosingConfirmation?.();
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      document.removeEventListener('click', beforeLink, true);
      window.Telegram?.WebApp?.disableClosingConfirmation?.();
    };
  }, [dirty]);

  const send = useCallback((message) => {
    frameRef.current?.contentWindow?.postMessage(message, ACADEMY_ORIGIN);
  }, []);
  const sendAppearance = useCallback(() => {
    const root = document.documentElement;
    const style = getComputedStyle(root);
    const variables = {canvas: '--bg', surface: '--bg2', ink: '--text', muted: '--text-muted', line: '--border'};
    const colors = Object.fromEntries(Object.entries(variables).map(([name, variable]) => [name, style.getPropertyValue(variable).trim()]));
    const telegram = window.Telegram?.WebApp;
    const theme = (actor === 'app' && telegram?.initData ? telegram.colorScheme : null) || root.dataset.theme || 'dark';
    send({type: 'hasket-portal-appearance', theme: theme === 'light' ? 'light' : 'dark', headerInHost: actor === 'app', colors});
  }, [actor, send]);
  const sendToken = useCallback(() => {
    if (auth.current.token) send({ type: 'hasket-portal-auth', token: auth.current.token });
    sendAppearance();
  }, [send, sendAppearance]);
  useEffect(() => {
    let frame = 0;
    const update = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(sendAppearance); };
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {attributes: true, attributeFilter: ['data-theme', 'style']});
    window.Telegram?.WebApp?.onEvent?.('themeChanged', update);
    sendAppearance();
    return () => { observer.disconnect(); cancelAnimationFrame(frame); window.Telegram?.WebApp?.offEvent?.('themeChanged', update); };
  }, [sendAppearance]);
  const refresh = useCallback(async (force = false) => {
    if (!ACADEMY_URL) return;
    if (!force && auth.current.token && auth.current.until > Date.now() + 30000) { sendToken(); return; }
    if (request.current) return request.current;
    const endpoint = actor === 'admin' ? '/api/admin/academy-token' : '/api/app/academy-token';
    request.current = api.get(endpoint).then(response => {
      if (!active.current) return;
      if (!response.data?.token) throw new Error('Missing Academy identity');
      const ttl = Math.max(60, Math.min(900, Number(response.data.expires_in) || 300));
      auth.current = {token: response.data.token, until: Date.now() + ttl * 1000};
      setCanEdit(response.data.can_edit === true);
      if (response.data.can_edit !== true) setView('learn');
      setReady(true);
      setError('');
      sendToken();
      clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => void refresh(true), ttl * 800);
    }).catch(() => {
      if (!active.current) return;
      setError('Не удалось подтвердить вход. Повторите попытку или войдите в портал заново.');
      send({ type: 'hasket-portal-auth-error' });
    }).finally(() => { request.current = null; });
    return request.current;
  }, [actor, send, sendToken]);

  useEffect(() => {
    active.current = true;
    void refresh();
    const onMessage = event => {
      if (event.origin !== ACADEMY_ORIGIN || event.source !== frameRef.current?.contentWindow) return;
      if (event.data?.type === 'hasket-academy-ready') void refresh(event.data.refresh === true);
      if (event.data?.type === 'hasket-academy-dirty') setDirty(event.data.dirty === true);
    };
    const onVisible = () => { if (document.visibilityState === 'visible') void refresh(); };
    window.addEventListener('message', onMessage);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active.current = false;
      clearTimeout(refreshTimer.current);
      window.removeEventListener('message', onMessage);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refresh]);

  if (!ACADEMY_URL) return <div className="academy-unavailable"><h1>Академия</h1><p>Раздел пока не подключён.</p></div>;
  return <div className="academy-frame-shell">
    {canEdit && <div className="academy-mode-bar" role="navigation" aria-label="Режим академии">
      <button type="button" className={view === 'learn' ? 'active' : ''} disabled={dirty && view !== 'learn'} onClick={() => setView('learn')}>Обучение</button>
      <button type="button" className={view === 'manage' ? 'active' : ''} disabled={dirty && view !== 'manage'} onClick={() => setView('manage')}>Управление обучением</button>
      {dirty && <span>Сохраните изменения перед сменой режима</span>}
    </div>}
    {error && <div className="academy-auth-error" role="alert"><span>{error}</span><button type="button" onClick={() => void refresh(true)}>Повторить</button></div>}
    {!ready && !error && <div className="academy-frame-loading"><span className="spinner"/><p>Открываем академию…</p></div>}
    {ready && !error && <iframe ref={frameRef} className="academy-frame" src={`${ACADEMY_URL}${view === 'manage' ? '/admin' : '/'}?embed=portal`} title={view === 'manage' ? 'Управление обучением Hasket' : 'Академия Hasket'} allow="clipboard-write" onLoad={sendToken}/>}
  </div>;
}
